import React, { useMemo, useState, useCallback } from 'react';
import { ChevronRight, ChevronDown, Download } from 'lucide-react';
import type { SortDirection } from '../../../types/common';
import type { RegionSummaryRow } from '../../../types/regionalPerformance';
import type { ParsedRegionalClient } from '../../../utils/regionalClientPerformanceUtils';

export interface RegionSummaryTableProps {
  rows: RegionSummaryRow[];
  clientsByRegion: Record<string, ParsedRegionalClient[]>;
}

type SortKey =
  | 'regionName'
  | 'uniqueClients'
  | 'revenue'
  | 'collectionRate'
  | 'shareOfRevenuePct';

interface ThProps {
  label: string;
  sortKey?: SortKey;
  activeSortKey: SortKey;
  sortDir: SortDirection;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
  className?: string;
}

const Th: React.FC<ThProps> = ({
  label,
  sortKey: sk,
  activeSortKey,
  sortDir,
  onSort,
  align = 'left',
  className = '',
}) => {
  const isActive = sk && activeSortKey === sk;
  return (
    <th
      scope="col"
      className={`
        px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap
        ${align === 'right' ? 'text-right' : 'text-left'}
        ${sk ? 'cursor-pointer select-none hover:text-slate-700' : ''}
        ${className}
      `}
      onClick={() => sk && onSort(sk)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (
          <span className="text-blue-500">{sortDir === 'asc' ? '↑' : '↓'}</span>
        )}
      </span>
    </th>
  );
};

function formatRegionMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${currency}`;
  }
}

/** When pipe had no original amounts, non-USD token still carries USD figures — format as USD. */
function clientAmountCurrency(c: ParsedRegionalClient): string {
  if (c.currency === 'USD') return 'USD';
  const revMatch = Math.abs(c.revenueOriginal - c.revenueUSD) < 0.005;
  const invMatch = Math.abs(c.invoicedOriginal - c.invoicedUSD) < 0.005;
  const outMatch =
    c.outstanding <= 0.005 ||
    Math.abs(c.outstandingOriginal - c.outstanding) < 0.005;
  return revMatch && invMatch && outMatch ? 'USD' : c.currency;
}

function downloadRegionalCSV(
  rows: RegionSummaryRow[],
  clientsByRegion: Record<string, ParsedRegionalClient[]>
) {
  if (rows.length === 0) return;
  const esc = (v: string) => (v.includes(',') ? `"${v.replace(/"/g, '""')}"` : v);
  const lines: string[] = [
    [
      'Row',
      'Region',
      'Name',
      'Clients / detail',
      'CCY',
      'Amount (display)',
      'USD ref',
      'Collection %',
      'Share %',
      'Out (display)',
      'Out USD (ref)',
      '% of region',
    ].join(','),
  ];
  for (const r of rows) {
    lines.push(
      [
        'region',
        esc(r.displayCode),
        esc(r.regionName),
        String(r.uniqueClients),
        r.displayCurrency,
        String(r.revenueDisplay),
        String(r.revenue),
        r.collectionRateDisplay.toFixed(2),
        r.shareOfRevenuePct.toFixed(2),
        '',
        '',
        '',
      ].join(',')
    );
    for (const c of clientsByRegion[r.selectionKey] || []) {
      const pct =
        r.revenue > 0 ? ((c.revenueUSD / r.revenue) * 100).toFixed(2) : '0';
      const meta = [
        c.currency,
        c.monthCount != null && c.monthCount > 1 ? `${c.monthCount} mo` : null,
      ]
        .filter(Boolean)
        .join(' · ');
      const invDisp = c.invoicedOriginal ?? c.invoicedUSD;
      const payCcy = clientAmountCurrency(c);
      const clientColl =
        invDisp > 0
          ? ((c.revenueOriginal / invDisp) * 100).toFixed(2)
          : '';
      lines.push(
        [
          'client',
          esc(r.displayCode),
          esc(c.name),
          esc(meta),
          payCcy,
          String(c.revenueOriginal),
          String(c.revenueUSD),
          clientColl,
          '',
          String(c.outstandingOriginal ?? c.outstanding ?? ''),
          String(c.outstanding ?? ''),
          pct,
        ].join(',')
      );
    }
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `regional_performance_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function RegionSummaryTable({
  rows,
  clientsByRegion,
}: RegionSummaryTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('revenue');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const handleSort = useCallback((key: SortKey) => {
    setSortKey(prev => {
      if (prev === key) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir(key === 'regionName' ? 'asc' : 'desc');
      return key;
    });
  }, []);

  const toggleRegion = useCallback((key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const sorted = useMemo(() => {
    const mult = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'string' && typeof bv === 'string') {
        return mult * av.localeCompare(bv);
      }
      return mult * ((av as number) - (bv as number));
    });
  }, [rows, sortKey, sortDir]);

  const isEmpty = rows.length === 0;

  if (isEmpty) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-md flex items-center justify-center h-40 text-sm text-slate-500">
        No regions for the selected period
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
          Regional drill-down
        </p>
        <button
          type="button"
          onClick={() => downloadRegionalCSV(rows, clientsByRegion)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          title="Export to CSV"
        >
          <Download size={13} />
          Export
        </button>
      </div>

      <div className="overflow-auto max-h-[min(75vh,56rem)]">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-slate-100 border-b-2 border-slate-200">
            <tr>
              <th
                className="w-10 min-w-[2.25rem] max-w-[2.25rem] px-1 py-2.5"
                aria-label="Expand"
              />
              <Th
                label="Region"
                sortKey="regionName"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                className="min-w-[11rem]"
              />
              <Th
                label="Clients"
                sortKey="uniqueClients"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
              />
              <Th
                label="Revenue"
                sortKey="revenue"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
              />
              <Th
                label="Coll %"
                sortKey="collectionRate"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
              />
              <Th
                label="Share"
                sortKey="shareOfRevenuePct"
                activeSortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
              />
            </tr>
          </thead>
          <tbody>
            {sorted.map(row => {
              const open = expanded.has(row.selectionKey);
              const clients = clientsByRegion[row.selectionKey] || [];

              return (
                <React.Fragment key={row.selectionKey}>
                  <tr
                    className="cursor-pointer select-none bg-white border-t border-slate-100 border-l-[3px] border-l-blue-500 hover:bg-blue-50/60 transition-colors text-sm"
                    onClick={() => toggleRegion(row.selectionKey)}
                  >
                    <td className="w-10 min-w-[2.25rem] max-w-[2.25rem] px-1 py-2.5 align-middle text-slate-500">
                      <span className="inline-flex" aria-hidden>
                        {open ? (
                          <ChevronDown size={14} strokeWidth={2} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={2} />
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 align-middle min-w-0 max-w-[min(22rem,40vw)]">
                      <span
                        className="block truncate font-semibold text-slate-900"
                        title={`${row.displayCode} · ${row.regionName}`}
                      >
                        {row.regionName}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-800">
                      {row.uniqueClients.toLocaleString('en-US')}
                    </td>
                    <td
                      className="px-3 py-2.5 align-middle text-right tabular-nums font-semibold text-slate-900"
                      title={
                        row.displayCurrency !== 'USD'
                          ? `USD ref: $${row.revenue.toLocaleString('en-US', {
                              maximumFractionDigits: 0,
                            })}`
                          : undefined
                      }
                    >
                      {formatRegionMoney(row.revenueDisplay, row.displayCurrency)}
                    </td>
                    <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-700">
                      {row.collectionRateDisplay.toFixed(1)}%
                    </td>
                    <td className="px-3 py-2.5 align-middle text-right tabular-nums text-slate-600">
                      {row.shareOfRevenuePct.toFixed(1)}%
                    </td>
                  </tr>

                  {open && (
                    <tr className="bg-slate-50/70 border-t border-slate-100">
                      <td colSpan={6} className="p-0 align-top">
                        {clients.length === 0 ? (
                          <p className="text-[12px] text-slate-500 px-3 py-2.5 pl-11">
                            No client list in source for this slice.
                          </p>
                        ) : (
                          <div className="max-h-[min(36rem,60vh)] overflow-auto pl-9 pr-2 py-2">
                            <table className="w-full border-collapse text-[12px]">
                              <thead>
                                <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                  <th className="py-1.5 pr-2 text-left w-8" scope="col">
                                    #
                                  </th>
                                  <th className="py-1.5 pr-2 text-left min-w-[8rem]" scope="col">
                                    Client
                                  </th>
                                  <th
                                    className="py-1.5 px-2 text-right whitespace-nowrap tabular-nums"
                                    scope="col"
                                  >
                                    Paid
                                  </th>
                                  <th
                                    className="py-1.5 px-2 text-right whitespace-nowrap tabular-nums"
                                    scope="col"
                                  >
                                    Invoiced
                                  </th>
                                  <th
                                    className="py-1.5 px-2 text-right whitespace-nowrap tabular-nums"
                                    scope="col"
                                  >
                                    Coll %
                                  </th>
                                  <th
                                    className="py-1.5 px-2 text-right whitespace-nowrap tabular-nums"
                                    scope="col"
                                    title="Share of region total revenue (USD basis)"
                                  >
                                    % Reg
                                  </th>
                                  <th
                                    className="py-1.5 pl-2 text-right whitespace-nowrap tabular-nums"
                                    scope="col"
                                    title="Primary figure in billing currency; USD in tooltip"
                                  >
                                    Outstanding
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {clients.map((c, idx) => {
                                  const pctRegion =
                                    row.revenue > 0
                                      ? (c.revenueUSD / row.revenue) * 100
                                      : 0;
                                  const paidDisp = c.revenueOriginal
                                  const invDisp =
                                    c.invoicedOriginal ?? c.invoicedUSD
                                  const payCcy = clientAmountCurrency(c)
                                  const collPct =
                                    invDisp > 0
                                      ? (paidDisp / invDisp) * 100
                                      : null;
                                  const usdRefPaid = `$${(c.revenueUSD || 0).toLocaleString('en-US', {
                                    maximumFractionDigits: 0,
                                  })}`
                                  const usdRefInv = `$${(c.invoicedUSD || 0).toLocaleString('en-US', {
                                    maximumFractionDigits: 0,
                                  })}`
                                  return (
                                    <tr
                                      key={`${row.selectionKey}-${c.clientId}-${c.name}`}
                                      className="border-b border-slate-100/90 bg-white/80 hover:bg-white"
                                    >
                                      <td className="py-2 pr-2 align-top">
                                        <span
                                          className={`inline-flex w-6 h-6 rounded-full items-center justify-center text-white text-[10px] font-bold ${
                                            idx === 0
                                              ? 'bg-amber-500'
                                              : idx === 1
                                                ? 'bg-slate-500'
                                                : idx === 2
                                                  ? 'bg-amber-500/90'
                                                  : 'bg-slate-400'
                                          }`}
                                        >
                                          {idx + 1}
                                        </span>
                                      </td>
                                      <td className="py-2 pr-2 align-top min-w-0 max-w-[14rem]">
                                        <div className="font-medium text-slate-900 truncate">
                                          {c.name}
                                        </div>
                                        <div className="text-[11px] text-slate-500 truncate mt-0.5">
                                          {c.currency}
                                          {c.monthCount != null && c.monthCount > 1 && (
                                            <span className="ml-1.5 px-1 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-semibold">
                                              {c.monthCount} mo
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td
                                        className="py-2 px-2 align-top text-right tabular-nums font-semibold text-slate-900 whitespace-nowrap"
                                        title={
                                          payCcy !== 'USD'
                                            ? `USD ref (paid): ${usdRefPaid}`
                                            : undefined
                                        }
                                      >
                                        {formatRegionMoney(paidDisp, payCcy)}
                                      </td>
                                      <td
                                        className="py-2 px-2 align-top text-right tabular-nums text-slate-700 whitespace-nowrap"
                                        title={
                                          payCcy !== 'USD'
                                            ? `USD ref (invoiced): ${usdRefInv}`
                                            : undefined
                                        }
                                      >
                                        {formatRegionMoney(invDisp, payCcy)}
                                      </td>
                                      <td className="py-2 px-2 align-top text-right tabular-nums text-slate-600 whitespace-nowrap">
                                        {collPct != null ? `${collPct.toFixed(1)}%` : '—'}
                                      </td>
                                      <td
                                        className="py-2 px-2 align-top text-right tabular-nums text-slate-600 whitespace-nowrap"
                                        title="Region total is USD; ratio uses client paid USD"
                                      >
                                        {row.revenue > 0 ? `${pctRegion.toFixed(1)}%` : '—'}
                                      </td>
                                      <td
                                        className="py-2 pl-2 align-top text-right tabular-nums whitespace-nowrap"
                                        title={
                                          payCcy !== 'USD' && c.outstanding > 0
                                            ? `USD ref: $${(c.outstanding || 0).toLocaleString('en-US', {
                                                maximumFractionDigits: 0,
                                              })}`
                                            : undefined
                                        }
                                      >
                                        {c.outstanding > 0 ? (
                                          <span className="text-rose-600">
                                            {formatRegionMoney(
                                              c.outstandingOriginal ?? c.outstanding,
                                              payCcy
                                            )}
                                          </span>
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
